import React from "react";
import { Upload, Download, CheckCircle, XCircle, FileText } from "lucide-react";
import Modal from "../../components/common/Modal";
import Typography from "../../components/common/Typography";
import Button from "../../components/common/Button";
import { Organisation } from "../../types/types";
import { useBulkUploadDialogHook } from "./useBulkUploadDialogHook";

interface BulkUploadDialogProps {
  open: boolean;
  onClose: () => void;
  organisation: Organisation | null;
  onSuccess: () => boolean | void | Promise<boolean | void>;
}

const BulkUploadDialog: React.FC<BulkUploadDialogProps> = ({
  open,
  onClose,
  organisation,
  onSuccess,
}) => {
  const {
    selectedFile,
    uploading,
    uploadResult,
    error,
    isDragging,
    fileInputRef,
    handleFileSelect,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDownloadTemplate,
    handleUpload,
    handleClose,
    handleTryAgain,
    canUpload,
  } = useBulkUploadDialogHook(organisation?.id, onSuccess, onClose);

  const renderUploadForm = () => (
    <div className="space-y-6">
      {/* Download Template */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Download className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <Typography variant="p" weight="medium" className="!text-blue-900">
              Download CSV Template
            </Typography>
            <Typography variant="small" className="!text-blue-700 mt-1">
              Use our template to ensure your data is formatted correctly. The
              template includes email, full_name, and role columns. Uploading
              stages staff as not activated; no invitations are sent until you
              activate them from the staff table.
            </Typography>
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              className="!mt-3 !text-[rgb(var(--pd-fg))] !border-[rgb(var(--pd-line))] hover:!bg-[rgb(var(--pd-surface))]"
              label="Download Template"
            />
          </div>
        </div>
      </div>

      {/* File Upload */}
      <div className="space-y-2">
        <Typography variant="p" weight="medium" className="!text-gray-700">
          Upload CSV File
        </Typography>
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragging
              ? "border-[rgb(var(--pd-focus))] bg-[rgb(var(--pd-surface))]"
              : selectedFile
                ? "border-green-300 bg-green-50"
                : "border-gray-300 hover:border-gray-400"
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
            id="csv-file-input"
          />
          <label
            htmlFor="csv-file-input"
            className="cursor-pointer flex flex-col items-center"
          >
            {isDragging ? (
              <>
                <Upload className="text-[rgb(var(--pd-fg-muted))] mb-2" size={32} />
                <Typography
                  variant="p"
                  weight="medium"
                  className="!text-[rgb(var(--pd-fg))]"
                >
                  Drop your CSV file here
                </Typography>
              </>
            ) : selectedFile ? (
              <>
                <FileText className="text-green-600 mb-2" size={32} />
                <Typography
                  variant="p"
                  weight="medium"
                  className="!text-green-700"
                >
                  {selectedFile.name}
                </Typography>
                <Typography variant="small" className="!text-green-600 mt-1">
                  Click to select a different file
                </Typography>
              </>
            ) : (
              <>
                <Upload className="text-gray-400 mb-2" size={32} />
                <Typography variant="p" className="!text-gray-600">
                  Click to select a CSV file
                </Typography>
                <Typography variant="small" className="!text-gray-400 mt-1">
                  or drag and drop
                </Typography>
              </>
            )}
          </label>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
          <XCircle className="text-red-500 flex-shrink-0" size={18} />
          <Typography variant="small" className="!text-red-700">
            {error}
          </Typography>
        </div>
      )}
    </div>
  );

  const renderResults = () => {
    if (!uploadResult) return null;

    const interrupted = Boolean(error);
    const hasFailures = uploadResult.failed > 0 || interrupted;

    return (
      <div className="space-y-4">
        {/* Summary */}
        <div
          className={`rounded-lg p-4 ${
            hasFailures
              ? "bg-yellow-50 border border-yellow-200"
              : "bg-green-50 border border-green-200"
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            {hasFailures ? (
              <XCircle className="text-yellow-600" size={20} />
            ) : (
              <CheckCircle className="text-green-600" size={20} />
            )}
            <Typography
              variant="p"
              weight="semibold"
              className={hasFailures ? "!text-yellow-800" : "!text-green-800"}
            >
              {interrupted
                ? "Upload interrupted"
                : hasFailures
                  ? "Upload completed with errors"
                  : "Staff uploaded"}
            </Typography>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-3">
            <div className="text-center">
              <Typography variant="h4" weight="bold" className="!text-gray-900">
                {uploadResult.total}
              </Typography>
              <Typography variant="small" className="!text-gray-500">
                {interrupted ? "Processed" : "Total"}
              </Typography>
            </div>
            <div className="text-center">
              <Typography
                variant="h4"
                weight="bold"
                className="!text-green-600"
              >
                {uploadResult.success}
              </Typography>
              <Typography variant="small" className="!text-gray-500">
                Uploaded, not activated
              </Typography>
            </div>
            <div className="text-center">
              <Typography variant="h4" weight="bold" className="!text-red-600">
                {uploadResult.failed}
              </Typography>
              <Typography variant="small" className="!text-gray-500">
                Failed
              </Typography>
            </div>
          </div>
        </div>

        {interrupted && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
            <XCircle className="text-red-500 flex-shrink-0" size={18} />
            <Typography variant="small" className="!text-red-700">
              {error}
            </Typography>
          </div>
        )}

        {/* Failed Rows Detail */}
        {uploadResult.failed > 0 && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
              <Typography
                variant="small"
                weight="medium"
                className="!text-gray-700"
              >
                Failed uploads:
              </Typography>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {uploadResult.results
                .filter((r) => !r.success)
                .map((result, index) => (
                  <div
                    key={index}
                    className="px-4 py-2 border-b border-gray-100 last:border-0"
                  >
                    <Typography
                      variant="small"
                      weight="medium"
                      className="!text-gray-900"
                    >
                      {result.email}
                    </Typography>
                    <Typography variant="small" className="!text-red-600">
                      {result.message || "Unknown error"}
                    </Typography>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const actions = (
    <div className="flex justify-end gap-3 w-full">
      <Button
        onClick={handleClose}
        disabled={uploading}
        variant="outline"
        label="Cancel"
      />
      {uploadResult ? (
        <Button
          onClick={handleTryAgain}
          variant="outline"
          label="Upload Another"
        />
      ) : (
        <Button
          onClick={handleUpload}
          disabled={!canUpload}
          variant="filled"
          label={uploading ? "Uploading..." : "Upload"}
        />
      )}
    </div>
  );

  return (
    <Modal
      activeModal={open}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[rgb(var(--pd-ink))] rounded-lg flex-shrink-0">
            <Upload className="text-white" size={20} />
          </div>
          <div className="flex-1">
            <Typography variant="h5" weight="semibold" className="!text-black">
              Bulk Upload Staff
            </Typography>
            {organisation?.name && (
              <Typography variant="small" className="!text-slate-400">
                {organisation.name}
              </Typography>
            )}
          </div>
        </div>
      }
      modalSize="medium"
      bodyClass="p-6"
      dialogClass="w-full min-w-0 max-w-[600px] mx-4"
      footerContent={actions}
    >
      {uploadResult ? renderResults() : renderUploadForm()}
    </Modal>
  );
};

export default BulkUploadDialog;
